import { useEffect, useRef, useState } from 'react';
import { sortByPosition } from '../ordering';
import { LEVELS_BY_KEY, TOPIC_HOST_LEVEL_KEYS } from '../courseLevels';
import { TOPIC_BLOCK_EDITOR_REGISTRY } from '../topicBlockEditors';
import { getTopicBlockType } from '../topicBlockTypes';
import { BlockTypeBadge } from './blockTypeIcons';
import BlurredPreviewWrapper from './BlurredPreviewWrapper';
import ExercisePreviewList from './ExercisePreviewList';
import PreviewBreadcrumb from './PreviewBreadcrumb';
import TopicLessonBadges from './TopicLessonBadges';
import ContainerLayout from '../../../components/ContainerLayout';
import { mergeTopicContent, TOPIC_ENTRY_KIND_BLOCK, TOPIC_ENTRY_KIND_CONTAINER } from '../topicContent';
import { safeParseContainerConfig } from '../../../variables/containerLayoutTypes';
import * as api from '../api';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';

// One TopicElement block's preview markup - shared between the plain
// top-level timeline and a container's own arrangement below, so both
// stay visually consistent.
export function BlockPreviewEntry({ block }) {
   const registryEntry = TOPIC_BLOCK_EDITOR_REGISTRY[block.type];
   if(!registryEntry) return null;
   const blockType = getTopicBlockType(block.type) || {};
   return (
      <div className='course-preview-block'>
         <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
         <div className='course-preview-block-content'>
            <BlurredPreviewWrapper elementType='TOPIC' elementId={block.id} blurEnabled={!!block.blurEnabled}>
               {registryEntry.renderPreview(block)}
            </BlurredPreviewWrapper>
         </div>
      </div>
   );
}

// A container's own children arranged per its layout config - each child
// gets a light card boundary (.course-preview-container-item) since a
// grid/wrap/table arrangement needs per-item legibility that the plain
// borderless connected-timeline treatment doesn't need (sequential flow +
// a connecting line already does that job there).
export function ContainerPreviewGroup({ container, elements }) {
   const children = mergeTopicContent({ elements, containers: [], containerId: container.id });
   if(children.length === 0) return null;
   const config = safeParseContainerConfig(container.config, container.layout);
   return (
      <ContainerLayout layout={container.layout} config={config}>
         {children.map(entry => (
            <div className='course-preview-container-item' key={entry.item.id}>
               <BlockPreviewEntry block={entry.item} />
            </div>
         ))}
      </ContainerLayout>
   );
}

// A linked exercise's preview, rendered inline in the timeline now instead
// of as a separate trailing section - see TopicElementList.jsx's own header
// comment for why linked exercises are now just another interleaved
// top-level entry kind rather than always-at-the-bottom.
export function ExerciseLinkPreviewEntry({ link }) {
   if (!link.exerciseTemplate) return null;
   return (
      <div className='course-preview-exercise'>
         <h2 className='course-preview-exercise-title'>{link.exerciseTemplate.name}</h2>
         <ExercisePreviewList exerciseTemplateId={link.exerciseTemplate.id} />
      </div>
   );
}

// Depth-first, position-ordered collection of every Topic nested under a
// given hierarchy node - lets clicking any level (Module/Chapter/Unit/
// Lesson, Course too), not just a Topic itself, preview "everything under
// here" in one scroll. A Topic can now attach directly to ANY of those
// levels (not just the deepest one), so at every level this collects that
// level's own directly-attached topics FIRST, then recurses into its
// structural children collecting theirs too - same "own content, then
// further structure" order the outline tree itself renders in.
export async function collectTopicsUnder(levelKey, id) {
   if(levelKey === 'topic') {
      const response = await api.getOne(LEVELS_BY_KEY.topic.apiBase, id);
      return [response.data];
   }
   const level = LEVELS_BY_KEY[levelKey];
   if(!level) return [];
   let topics = [];

   if(level.canHostTopics) {
      const topicResponse = await api.getList(LEVELS_BY_KEY.topic.apiBase, level.key, id);
      const ownTopics = sortByPosition(Array.isArray(topicResponse.data) ? topicResponse.data : []);
      // Hidden Topics never show in the "how a student sees this" preview -
      // Course itself has no `visible` column (ceiling of the toggle's
      // scope), so this filter only ever excludes something starting here.
      topics = topics.concat(ownTopics.filter(t => t.visible !== false));
   }

   if(level.childKey) {
      const childLevel = LEVELS_BY_KEY[level.childKey];
      const childResponse = await api.getList(childLevel.apiBase, childLevel.filterParam, id);
      // A hidden Module/Chapter/Unit/CourseLesson hides everything nested
      // under it too - never recurse into an invisible child.
      const children = sortByPosition(Array.isArray(childResponse.data) ? childResponse.data : [])
         .filter(c => c.visible !== false);
      for(const child of children) {
         const childTopics = await collectTopicsUnder(childLevel.key, child.id);
         topics = topics.concat(childTopics);
      }
   }

   return topics;
}

// One topic's own flattened content: a breadcrumb for THIS topic specifically
// (so scrolling through several topics in a row always shows where you are,
// not just once at the top of the whole panel), then header, content
// blocks, then each linked exercise's own blocks - self-contained fetch,
// same shape as before, just extracted so multiple of these can be stacked
// in sequence.
//
// A topic attached directly to the Course root (no Module/Chapter/Unit/
// Lesson at all) isn't really "a Topic" from a reader's point of view -
// it's the closest thing this hierarchy has to general descriptive content
// for the course itself (an "About this course" block, an intro, etc.),
// per explicit user request. So it drops the "Тема" eyebrow that every
// other topic gets (nothing here marks it as belonging to a type) - just
// title + description + content, same as any other topic underneath. Its
// breadcrumb would be empty too (nothing above it besides the Course, which
// PreviewBreadcrumb never renders) so it's skipped entirely rather than
// rendering an empty wrapper. Determined from the topic's OWN 5 host fields
// (same lookup PreviewBreadcrumb.jsx's nextAncestor already does) rather
// than trusting the caller to know - self-contained, works regardless of
// how this component got reached.
//
// `sectionRef` is a plain DOM ref callback (not a React ref object) so the
// parent's IntersectionObserver-based scroll-spy can observe this section's
// actual root element directly - passed straight onto that element rather
// than adding an extra wrapper div, which would break the adjacent-sibling
// CSS divider between consecutive sections (`.course-preview-topic-section
// + .course-preview-topic-section`).
function TopicPreviewSection({ topic, actions, sectionRef }) {
   const [elements, setElements] = useState([]);
   const [containers, setContainers] = useState([]);
   const [links, setLinks] = useState([]);
   const isRootTopic = TOPIC_HOST_LEVEL_KEYS.find((key) => topic[key]) === 'course';

   useEffect(() => {
      api.getList(TOPIC_ELEMENT_BASE, 'topic', topic.id)
         .then(response => setElements(Array.isArray(response.data) ? response.data : []))
         .catch(() => setElements([]));
      api.getList(TOPIC_CONTAINER_BASE, 'topic', topic.id)
         .then(response => setContainers(Array.isArray(response.data) ? response.data : []))
         .catch(() => setContainers([]));
      api.getTopicExerciseTemplates(topic.id)
         .then(response => setLinks(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLinks([]));
   }, [topic.id]);

   const topLevel = mergeTopicContent({ elements, containers, exerciseLinks: links, containerId: null });
   const isEmpty = topLevel.length === 0;

   return (
      <div className='course-preview-topic-section' ref={sectionRef} data-topic-id={topic.id}>
         {isRootTopic ? '' : <PreviewBreadcrumb selected={{ levelKey: 'topic', id: topic.id }} actions={actions} />}
         <div className='course-preview-header'>
            {isRootTopic ? '' : <span className='course-preview-eyebrow'>Тема</span>}
            <h1 className='course-preview-title'>{topic.name}</h1>
            {topic.description ? <p className='course-preview-description'>{topic.description}</p> : ''}
            <TopicLessonBadges topicId={topic.id} />
            <div className='course-preview-header-rule' />
         </div>

         {topLevel.length > 0 ? (
            <div className='course-preview-timeline'>
               {topLevel.map(entry => (
                  entry.kind === TOPIC_ENTRY_KIND_BLOCK
                     ? <BlockPreviewEntry key={'el-' + entry.item.id} block={entry.item} />
                     : entry.kind === TOPIC_ENTRY_KIND_CONTAINER
                     ? <ContainerPreviewGroup key={'container-' + entry.item.id} container={entry.item} elements={elements} />
                     : <ExerciseLinkPreviewEntry key={'ex-' + entry.item.id} link={entry.item} />
               ))}
            </div>
         ) : ''}

         {isEmpty ? <p className='course-preview-empty-hint'>{isRootTopic ? 'Тут ще немає контенту.' : 'У цій темі ще немає контенту.'}</p> : ''}
      </div>
   );
}

// Flattened "how a student will actually see this" rendering for WHATEVER
// is selected in the outline tree - not just a Topic, and now the Course
// root itself too (PreviewNavList.jsx's new course row) - collectTopicsUnder
// was already generic over any hostable levelKey, it just had no click
// target pointed at 'course' before this. Clicking a Module/Chapter/Unit/
// Lesson/Course previews every Topic nested under it, in order, starting
// from the first one and continuing through the rest.
//
// Clicking a single Topic directly is a deliberate exception to "only what's
// nested under here": per explicit user request, it shows that topic AND
// every topic AFTER it in the whole COURSE's own order (not just that one
// topic in isolation) - "start reading from here, keep going through the
// rest of the course," same as clicking the Course root itself would, just
// scoped to start partway through instead of from the beginning. Fetches
// the WHOLE course's flattened topic list (collectTopicsUnder('course', ...)
// - already exactly this walk) and slices from the clicked topic's own
// position onward, rather than a separate/duplicated query.
//
// The breadcrumb used to render once at the top of the whole panel (for
// whatever was clicked) - now each TopicPreviewSection renders its OWN
// breadcrumb instead (for that specific topic), so scrolling through
// several topics in a row always shows which one you're currently reading,
// not just where you started.
//
// `actions` is threaded down into each section for PreviewBreadcrumb's
// click-to-navigate (jump straight to an ancestor level) - collectTopicsUnder
// etc. below still always fetch fresh rather than reading the constructor's
// tree cache, that part is unchanged.
//
// `onActiveTopicChange` (new) reports which topic is currently scrolled
// into view, via one shared IntersectionObserver watching every section's
// root element - a section counts as "current" once its top has crossed
// into the upper ~35% of the viewport (rootMargin below), and among
// whichever sections are inside that band at once, the LAST one in
// (position-ordered) document order wins - standard scrollspy behavior:
// the heading you most recently scrolled past is "current," not one
// further down you haven't reached yet. Resets to null whenever the
// topic list itself changes (a new selection was made) so a stale marker
// from the previous view can't linger in the nav for the instant before
// the new observer has anything to report.
export default function TopicPreviewPanel({ courseId, selected, actions, onActiveTopicChange }) {
   const [state, setState] = useState({ loading: false, topics: [] });
   const sectionElsRef = useRef(new Map());
   const inViewRef = useRef(new Set());

   useEffect(() => {
      if(!selected) {
         setState({ loading: false, topics: [] });
         return;
      }
      setState({ loading: true, topics: [] });
      if(selected.levelKey === 'topic') {
         collectTopicsUnder('course', courseId)
            .then(allTopics => {
               const index = allTopics.findIndex(t => t.id === selected.id);
               const topics = index === -1 ? [] : allTopics.slice(index);
               setState({ loading: false, topics });
            })
            .catch(() => setState({ loading: false, topics: [] }));
      } else {
         collectTopicsUnder(selected.levelKey, selected.id)
            .then(topics => setState({ loading: false, topics }))
            .catch(() => setState({ loading: false, topics: [] }));
      }
   }, [selected, courseId]);

   useEffect(() => {
      sectionElsRef.current.clear();
      inViewRef.current.clear();
      if(onActiveTopicChange) onActiveTopicChange(null);
   }, [selected, onActiveTopicChange]);

   useEffect(() => {
      if(!onActiveTopicChange || state.topics.length === 0) return undefined;
      const orderedIds = state.topics.map(t => t.id);
      const observer = new IntersectionObserver((entries) => {
         entries.forEach((entry) => {
            const topicId = Number(entry.target.dataset.topicId);
            if(entry.isIntersecting) inViewRef.current.add(topicId);
            else inViewRef.current.delete(topicId);
         });
         const active = orderedIds.filter(id => inViewRef.current.has(id)).pop();
         if(active != null) onActiveTopicChange(active);
      }, { rootMargin: '0px 0px -65% 0px', threshold: 0 });
      sectionElsRef.current.forEach((el) => { if(el) observer.observe(el); });
      return () => observer.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.topics]);

   const registerSectionEl = (topicId, el) => {
      if(el) sectionElsRef.current.set(topicId, el);
      else sectionElsRef.current.delete(topicId);
   };

   if(!selected) {
      return <div className='course-preview-empty'>Оберіть елемент у дереві, щоб побачити перегляд.</div>;
   }

   return (
      <div className='course-preview'>
         {state.loading ? (
            <div className='course-preview-empty'>Завантаження...</div>
         ) : state.topics.length === 0 ? (
            <div className='course-preview-empty'>Тут ще немає жодної теми з контентом.</div>
         ) : (
            state.topics.map(topic => (
               <TopicPreviewSection key={topic.id} topic={topic} actions={actions}
                  sectionRef={(el) => registerSectionEl(topic.id, el)} />
            ))
         )}
      </div>
   );
}
